import { useCallback, useEffect, useRef, useState } from "react";
import { getSprintResumo, getSprintDevs, getSprintDados, getSprintProgramadosDetalhado, clearCache } from "../api/sprint.api";
import { getSetupSprint } from "../api/setup.api";
import TicketDrawer from "../components/TicketDrawer";

const REFRESH_SEC = 40;

const FECHADOS_STATUS = [
  "Fechado", "Resolvido", "Reteste", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
];
const isClosed = (s) => FECHADOS_STATUS.includes(s);

export default function Sprint() {
  const [resumo,       setResumo]       = useState(null);
  const [devs,         setDevs]         = useState([]);
  const [sprintConfig, setSprintConfig] = useState(null);
  const [countdown,    setCountdown]    = useState(REFRESH_SEC);
  const [drawer,       setDrawer]       = useState({ open: false, fetchId: 0 });
  const loadRef = useRef(null);

  const openDrawer  = useCallback((cfg) => setDrawer(prev => ({ ...cfg, open: true, fetchId: prev.fetchId + 1 })), []);
  const closeDrawer = useCallback(() => setDrawer(prev => ({ ...prev, open: false })), []);

  async function load(isRefresh = false) {
    try {
      if (isRefresh) clearCache();
      const [r, d, s] = await Promise.allSettled([
        getSprintResumo(), getSprintDevs(), getSetupSprint(),
      ]);
      if (r.status === "fulfilled") setResumo(r.value);
      if (d.status === "fulfilled") setDevs(d.value.developers || []);
      if (s.status === "fulfilled") setSprintConfig(s.value);
    } catch (err) {
      console.error(err);
    }
  }

  loadRef.current = load;

  function handleRefresh() {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
  }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (!resumo?.saida?.geral) return <SprintSkeleton />;

  const { total, aberto: abertos, fechado: fechados } = resumo.saida.geral;
  const pct      = resumo.saida.geral.percentual ?? (total ? Math.round((fechados / total) * 100) : 0);
  const pctColor = pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#f87171";

  /* ── Totais de Programados e Emergências via /sprint/devs ── */
  const progTotal   = devs.reduce((s, d) => s + (d.programado?.total   || 0), 0);
  const progFechado = devs.reduce((s, d) => s + (d.programado?.fechado || 0), 0);
  const progAberto  = devs.reduce((s, d) => s + (d.programado?.aberto  || 0), 0);
  const progPct     = progTotal ? Math.round((progFechado / progTotal) * 100) : 0;

  const emrgTotal   = devs.reduce((s, d) => s + (d.emergencia?.total   || 0), 0);
  const emrgFechado = devs.reduce((s, d) => s + (d.emergencia?.fechado || 0), 0);
  const emrgAberto  = devs.reduce((s, d) => s + (d.emergencia?.aberto  || 0), 0);
  const emrgPct     = emrgTotal ? Math.round((emrgFechado / emrgTotal) * 100) : 0;

  /* ── Ranking ── */
  const ranked = [...devs].map(d => {
    const prog      = d.programado || { total: 0, aberto: 0, fechado: 0 };
    const emrg      = d.emergencia || { total: 0, aberto: 0, fechado: 0 };
    const totalAll  = prog.total  + emrg.total;
    const totalFech = prog.fechado + emrg.fechado;
    const totalAbert= prog.aberto  + emrg.aberto;
    const pctDev    = totalAll ? Math.round((totalFech / totalAll) * 100) : 0;
    const score     = totalFech * 20 + pctDev * 0.5 - totalAbert * 3;
    return { ...d, prog, emrg, totalAll, totalFech, totalAbert, pctDev, score };
  }).sort((a, b) => b.score - a.score);

  /* ── Alertas: devs com < 40% de conclusão e com tickets ── */
  const alerts = ranked.filter(d => d.pctDev < 40 && d.totalAll > 0);

  /* ── Sprint Score ── */
  const scoreData = (() => {
    // Conta dias úteis (seg–sex) entre duas datas, inclusive
    function diasUteis(a, b) {
      let n = 0;
      const cur = new Date(a); cur.setHours(0,0,0,0);
      const end = new Date(b); end.setHours(0,0,0,0);
      while (cur <= end) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) n++;
        cur.setDate(cur.getDate() + 1);
      }
      return n;
    }

    // Componente 1 — conclusão (0–50 pts)
    const cPct = Math.round(pct * 0.5);

    // Componente 2 — ritmo de burndown (0–30 pts)
    let cRitmo = 15; // neutro se sem datas
    let diasRestantes = null;
    let diasTotais = null;
    let idealPct = null;
    if (sprintConfig?.data_inicial && sprintConfig?.data_final) {
      const hoje   = new Date(); hoje.setHours(0,0,0,0);
      const inicio = new Date(sprintConfig.data_inicial.replace(" ", "T"));
      const fim    = new Date(sprintConfig.data_final.replace(" ", "T"));
      diasTotais    = Math.max(1, diasUteis(inicio, fim));
      diasRestantes = hoje > fim ? 0 : diasUteis(hoje, fim);
      const decorridos = Math.max(0, diasTotais - diasRestantes);
      idealPct = Math.round((decorridos / diasTotais) * 100);
      const delta = pct - idealPct;
      cRitmo = delta >= 5 ? 30 : delta >= 0 ? 25 : delta >= -10 ? 18 : delta >= -20 ? 10 : 3;
    }

    // Componente 3 — saúde do time (0–20 pts)
    const activeDevs = ranked.filter(d => d.totalAll > 0).length;
    const cTime = activeDevs > 0 ? Math.round((1 - alerts.length / activeDevs) * 20) : 20;

    const score = Math.min(100, cPct + cRitmo + cTime);
    const { label, color, grade } =
      score >= 80 ? { label: "Excelente",   color: "#22d3ee", grade: "A" } :
      score >= 65 ? { label: "Saudável",    color: "#4ade80", grade: "B" } :
      score >= 50 ? { label: "Atenção",     color: "#fbbf24", grade: "C" } :
      score >= 30 ? { label: "Em Risco",    color: "#fb923c", grade: "D" } :
                    { label: "Crítico",     color: "#f87171", grade: "F" };

    return { score, cPct, cRitmo, cTime, label, color, grade, diasRestantes, diasTotais, idealPct };
  })();


  const devColor = (p) => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

  /* ── Fetch helpers para o drawer ── */
  const fetchTotal    = () => getSprintDados();
  const fetchAbertos  = () => getSprintDados().then(d => d.filter(t => !isClosed(t.status)));
  const fetchFechados = () => getSprintDados().then(d => d.filter(t => isClosed(t.status)));
  const fetchProg     = () => getSprintProgramadosDetalhado();
  const fetchEmrg     = () => getSprintDados().then(d => d.filter(t => !t.programado || t.programado === "0" || t.programado === 0 || t.programado === false));
  const fetchDev      = (nome) => () => getSprintDados().then(d => d.filter(t => t.desenvolvedor === nome));

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={headerLeft}>
          <h1 style={h1}>Sprint</h1>
          <span style={badge}>Atual</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleRefresh}
            title="Atualizar agora"
            style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── KPI Row ── */}
      <div style={kpiRow}>
        <KpiCard label="Total"     value={total}     color="#60a5fa" onClick={() => openDrawer({ title: "Total de Tickets",   subtitle: "Todos os tickets trabalhados na sprint",   color: "#60a5fa", fetchTickets: fetchTotal })} />
        <KpiCard label="Abertos"   value={abertos}   color="#f87171" onClick={() => openDrawer({ title: "Tickets Abertos",    subtitle: "Tickets ainda em andamento",               color: "#f87171", fetchTickets: fetchAbertos })} />
        <KpiCard label="Fechados"  value={fechados}  color="#4ade80" onClick={() => openDrawer({ title: "Tickets Fechados",   subtitle: "Tickets concluídos na sprint",             color: "#4ade80", fetchTickets: fetchFechados })} />
        <KpiCard label="Conclusão" value={`${pct}%`} color={pctColor}
          extra={<ProgressBar pct={pct} color={pctColor} />}
        />
      </div>

      {/* ── Middle: Prog | Emerg | Saúde ── */}
      <div style={midGrid}>

        {/* Programados */}
        <SplitCard
          icon="◎" label="Programados" color="#22d3ee"
          total={progTotal} fechado={progFechado} aberto={progAberto} pct={progPct}
          onClick={() => openDrawer({ title: "Programados", subtitle: "Tickets da lista de programação da sprint", color: "#22d3ee", fetchTickets: fetchProg })}
        />

        {/* Emergências */}
        <SplitCard
          icon="⚡" label="Emergências" color="#a78bfa"
          total={emrgTotal} fechado={emrgFechado} aberto={emrgAberto} pct={emrgPct}
          onClick={() => openDrawer({ title: "Emergências", subtitle: "Tickets não planejados atendidos na sprint", color: "#a78bfa", fetchTickets: fetchEmrg })}
        />

        {/* Sprint Score */}
        <div style={glass}>
          <SectionTitle>Sprint Score</SectionTitle>

          {/* Score principal */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{
              width: 58, height: 58, borderRadius: "50%", flexShrink: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              background: `${scoreData.color}14`,
              border: `3px solid ${scoreData.color}60`,
              boxShadow: `0 0 16px ${scoreData.color}25`,
            }}>
              <span style={{ fontSize: 22, fontWeight: 900, color: scoreData.color, lineHeight: 1 }}>{scoreData.score}</span>
              <span style={{ fontSize: 9, color: scoreData.color, fontWeight: 700, opacity: 0.8 }}>{scoreData.grade}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: scoreData.color, lineHeight: 1 }}>{scoreData.label}</div>
              {scoreData.diasRestantes !== null && (
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                  {scoreData.diasRestantes === 0 ? "Prazo encerrado hoje" :
                   scoreData.diasRestantes === 1 ? "1 dia útil restante" :
                   `${scoreData.diasRestantes} dias restantes`}
                  {scoreData.idealPct !== null && (
                    <span style={{ marginLeft: 5, color: pct >= scoreData.idealPct ? "#4ade80" : "#f87171" }}>
                      · ideal {scoreData.idealPct}%
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Breakdown do score */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {[
              { label: "Conclusão",   pts: scoreData.cPct,   max: 50, color: "#60a5fa" },
              { label: "Ritmo",       pts: scoreData.cRitmo, max: 30, color: "#a78bfa" },
              { label: "Time",        pts: scoreData.cTime,  max: 20, color: "#fb923c" },
            ].map(({ label, pts, max, color }) => (
              <div key={label}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#475569", marginBottom: 2 }}>
                  <span>{label}</span>
                  <span style={{ color }}>{pts}/{max}</span>
                </div>
                <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(pts / max) * 100}%`, background: color, borderRadius: 3, transition: "width 0.6s" }} />
                </div>
              </div>
            ))}
          </div>

          {/* Alertas do time */}
          {alerts.length > 0 ? (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 5 }}>
                ⚠ Abaixo de 40%
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {alerts.map((a, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", background: "rgba(248,113,113,0.07)", borderRadius: 6, border: "1px solid rgba(248,113,113,0.15)" }}>
                    <span style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 500 }}>{a.nome}</span>
                    <span style={{ fontSize: 11, color: "#f87171", fontWeight: 700 }}>{a.pctDev}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: "#4ade80", display: "flex", alignItems: "center", gap: 5 }}>
              <span>✓</span><span>Time acima de 40%</span>
            </div>
          )}
        </div>

      </div>

      {/* ── Ranking ── */}
      <div style={{ ...glass }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
          <SectionTitle style={{ marginBottom: 0 }}>Ranking de Desenvolvedores</SectionTitle>
          <span style={{ fontSize: 10, color: "#334155", fontStyle: "italic" }}>
            pontuação = tickets fechados × 20 + conclusão × 0,5 − tickets abertos × 3
          </span>
        </div>
        <div>
          <div style={rankGrid}>
            {ranked.map((d, i) => (
              <DevCard
                key={i} dev={d} pos={i} devColor={devColor}
                onClick={() => openDrawer({ title: d.nome, subtitle: "Tickets atribuídos a este desenvolvedor", color: devColor(d.pctDev), fetchTickets: fetchDev(d.nome) })}
              />
            ))}
          </div>
        </div>
      </div>

      <TicketDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.title}
        subtitle={drawer.subtitle}
        color={drawer.color}
        fetchTickets={drawer.fetchTickets}
        fetchId={drawer.fetchId}
      />

    </div>
  );
}

/* ── Sub-components ─────────────────────────────────── */

function SectionTitle({ children, style }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12, flexShrink: 0, ...style }}>
      {children}
    </div>
  );
}

function KpiCard({ label, value, color, extra, onClick }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px ${color}20`; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; } : undefined}
      style={{ ...glass, borderTop: `2px solid ${color}`, justifyContent: "center", gap: 5, cursor: onClick ? "pointer" : "default", transition: "transform 0.15s, box-shadow 0.15s" }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, letterSpacing: -1, lineHeight: 1 }}>{value}</div>
      {extra}
    </div>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginTop: 4 }}>
      <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, boxShadow: `0 0 8px ${color}50`, transition: "width 0.6s" }} />
    </div>
  );
}

function SplitCard({ icon, label, color, total, fechado, aberto, pct, onClick }) {
  const pc = pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#f87171";
  return (
    <div
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.transform = "translateY(-2px)"; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.transform = ""; } : undefined}
      style={{ ...glass, borderTop: `2px solid ${color}`, cursor: onClick ? "pointer" : "default", transition: "transform 0.15s" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
        <span style={{ fontSize: 15, color }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2 }}>{label}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
        <MiniStat label="Total"    value={total}   color="#94a3b8" />
        <MiniStat label="Fechados" value={fechado}  color="#4ade80" />
        <MiniStat label="Abertos"  value={aberto}   color="#f87171" />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 10, color: "#475569" }}>Conclusão</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: pc }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${pc}99, ${pc})`, borderRadius: 4, boxShadow: `0 0 8px ${pc}40`, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function DevCard({ dev: d, pos: i, devColor, onClick }) {
  const c        = devColor(d.pctDev);
  const medal    = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
  const progPct  = d.prog.total ? Math.round((d.prog.fechado / d.prog.total) * 100) : 0;
  const emrgPct  = d.emrg.total ? Math.round((d.emrg.fechado / d.emrg.total) * 100) : 0;
  const pc       = (p) => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

  return (
    <div
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${c}35`; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(96,165,250,0.08)"; e.currentTarget.style.transform = ""; }}
      style={{ ...devCard, cursor: "pointer", transition: "border-color 0.15s, transform 0.15s" }}
    >

      {/* ── Topo: posição + nome + % ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>
            {medal || <span style={{ color: "#475569", fontSize: 13, fontWeight: 700 }}>#{i + 1}</span>}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.nome}
          </span>
        </div>
        <span style={{ fontSize: 22, fontWeight: 800, color: c, flexShrink: 0, marginLeft: 8 }}>
          {d.pctDev}%
        </span>
      </div>

      {/* ── Barra de conclusão geral ── */}
      <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ height: "100%", width: `${d.pctDev}%`, borderRadius: 3, background: c, boxShadow: `0 0 8px ${c}40`, transition: "width 0.6s ease" }} />
      </div>

      {/* ── Score ── */}
      <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, marginBottom: 12 }}>⭐ {d.score} pontos</div>

      {/* ── Bloco Programados ── */}
      {d.prog.total > 0 && (
        <div style={statBlock}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#22d3ee", textTransform: "uppercase", letterSpacing: 0.7 }}>◎ Programados</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: pc(progPct) }}>{progPct}%</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 6 }}>
            <StatCell label="Fechados" value={d.prog.fechado} color="#4ade80" />
            <StatCell label="Abertos"  value={d.prog.aberto}  color="#f87171" />
            <StatCell label="Total"    value={d.prog.total}   color="#94a3b8" />
          </div>
          <div style={{ height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progPct}%`, background: "#22d3ee", borderRadius: 2, transition: "width 0.6s ease" }} />
          </div>
        </div>
      )}

      {/* ── Bloco Emergências ── */}
      {d.emrg.total > 0 && (
        <div style={{ ...statBlock, borderColor: "rgba(167,139,250,0.15)", marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", textTransform: "uppercase", letterSpacing: 0.7 }}>⚡ Emergências</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: pc(emrgPct) }}>{emrgPct}%</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 6 }}>
            <StatCell label="Fechados" value={d.emrg.fechado} color="#4ade80" />
            <StatCell label="Abertos"  value={d.emrg.aberto}  color="#f87171" />
            <StatCell label="Total"    value={d.emrg.total}   color="#94a3b8" />
          </div>
          <div style={{ height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${emrgPct}%`, background: "#a78bfa", borderRadius: 2, transition: "width 0.6s ease" }} />
          </div>
        </div>
      )}

    </div>
  );
}

function StatCell({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", padding: "5px 4px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "7px 8px", textAlign: "center", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function CountdownRing({ seconds, total }) {
  const r      = 11;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 10;
  const color  = urgent ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeDasharray={circ}
          strokeDashoffset={circ - filled}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
          próx. atualiz.
        </span>
      </div>
    </div>
  );
}

function SprintSkeleton() {
  return (
    <div style={page}>
      <div style={{ ...skel, height: 36, width: 200, marginBottom: 4 }} />
      <div style={{ display: "flex", gap: 12 }}>
        {[0, 1, 2, 3].map(i => <div key={i} style={{ ...skel, height: 88, flex: 1 }} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        {[0, 1, 2].map(i => <div key={i} style={{ ...skel, height: 130 }} />)}
      </div>
      <div style={{ ...skel, flex: 1 }} />
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────── */

const page = { display: "flex", flexDirection: "column", gap: 14 };

const header     = { display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 };
const headerLeft = { display: "flex", alignItems: "center", gap: 12 };

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };

const badge = { padding: "3px 12px", borderRadius: 20, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 12, fontWeight: 700 };

const refreshBtn = {
  width: 34, height: 34, borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.08)",
  color: "#60a5fa", fontSize: 18, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0, lineHeight: 1,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "16px 18px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  display: "flex",
  flexDirection: "column",
};

const kpiRow = { display: "flex", gap: 12 };

const midGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 };

const rankGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  alignItems: "start",
  gap: 12,
  paddingBottom: 4,
};

const devCard = {
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(96,165,250,0.08)",
  borderRadius: 12,
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
};

const statBlock = {
  background: "rgba(34,211,238,0.04)",
  border: "1px solid rgba(34,211,238,0.12)",
  borderRadius: 8,
  padding: "10px 10px 8px",
};

const skel = { borderRadius: 14, background: "rgba(255,255,255,0.04)" };
